import { TFunction } from 'i18next';
import { IntegrationConfigBase } from 'models/integrations/integrationConfigBase';
import { IIntegrationEvidence } from 'models/integrations/integrationEvidence';
import { toLocaleDateTime } from 'utils/datetime';

//
// The three guest role template ids of the authorization policy (API MS_GuestAccessRestrictionHandler.GuestRoles)
//
export const GuestRoleIds = {
  User: 'a0b1b346-4d3e-4e8b-98f8-753987be4970',
  GuestUser: '10dae51f-b6af-4016-8d66-8c2a99b929b3',
  RestrictedGuestUser: '2af84b1e-32c8-42b7-82bc-daa82404023b',
};

//
// Config class: one parameter, also pass on the Guest User role (limited access)
//
export class IntegrationConfig_MS_GuestAccessRestriction extends IntegrationConfigBase {
  acceptLimitedGuestRole: boolean = false;
}

//
// Evidence class: the guest role read, the toggle the run used, the outcome and when it was read
//
export class MS_GuestAccessRestrictionEvidence implements IIntegrationEvidence {
  isCapped: boolean = false;

  evaluatedAtUtc: Date = new Date();

  //undefined when Graph did not answer the property: shown as unknown, never as a pass
  guestUserRoleId: string | undefined = undefined;

  //the English role name, the raw id for an id outside the three
  guestUserRoleName: string | undefined = undefined;

  acceptLimitedGuestRole: boolean = false;

  pass: boolean = false;

  //the option as the Entra admin center words it; an unknown id raw, a missing one as unknown
  getFoundText(t: TFunction<string[]>): string {
    if (!this.guestUserRoleId) return t('integrations:MS_GuestAccessRestriction.Result.Unknown');

    return getGuestRoleText(this.guestUserRoleId, t) ?? this.guestUserRoleId;
  }

  getExpectedText(t: TFunction<string[]>): string {
    return getExpectedText(this.acceptLimitedGuestRole, t);
  }

  toMarkdownReport(t: TFunction<['translation', 'integrations']>): string {
    const sb: string[] = [];

    sb.push(
      `## ${t('integrations:MS_GuestAccessRestriction.Report.SummaryHeading', {
        date: toLocaleDateTime(this.evaluatedAtUtc),
      })}`,
    );
    sb.push(
      `- ${t('integrations:MS_GuestAccessRestriction.Report.SettingLine', {
        found: this.getFoundText(t),
        expected: this.getExpectedText(t),
      })}`,
    );
    sb.push(
      `- ${t('integrations:MS_GuestAccessRestriction.Report.AcceptLimitedLine', {
        value: this.acceptLimitedGuestRole ? t('translation:General.Words.Yes') : t('translation:General.Words.No'),
      })}`,
    );
    sb.push(
      `- ${t('integrations:MS_GuestAccessRestriction.Report.OutcomeLine', {
        outcome: this.pass
          ? t('integrations:MS_GuestAccessRestriction.Result.Pass')
          : t('integrations:MS_GuestAccessRestriction.Result.Fail'),
      })}`,
    );

    return sb.join('\n');
  }
}

//the option sentence of one of the three roles; undefined for any other id
export const getGuestRoleText = (roleId: string, t: TFunction<string[]>): string | undefined => {
  switch (roleId.toLowerCase()) {
    case GuestRoleIds.User:
      return t('integrations:MS_GuestAccessRestriction.Roles.User');
    case GuestRoleIds.GuestUser:
      return t('integrations:MS_GuestAccessRestriction.Roles.Guest');
    case GuestRoleIds.RestrictedGuestUser:
      return t('integrations:MS_GuestAccessRestriction.Roles.Restricted');
    default:
      return undefined;
  }
};

//the expected value follows the toggle: the restricted option, or restricted or limited access
export const getExpectedText = (acceptLimitedGuestRole: boolean, t: TFunction<string[]>): string => {
  return acceptLimitedGuestRole
    ? t('integrations:MS_GuestAccessRestriction.Roles.RestrictedOrLimited')
    : t('integrations:MS_GuestAccessRestriction.Roles.Restricted');
};

export const parse_Evidence_MS_GuestAccessRestriction = (
  raw: string | undefined,
): MS_GuestAccessRestrictionEvidence | undefined => {
  if (!raw) return undefined;

  const rawEvidence = JSON.parse(raw);
  if (!rawEvidence) return undefined;

  const evidence = new MS_GuestAccessRestrictionEvidence();
  evidence.isCapped = rawEvidence.isCapped ?? false;
  evidence.evaluatedAtUtc = rawEvidence.evaluatedAtUtc ? new Date(rawEvidence.evaluatedAtUtc) : new Date();
  evidence.guestUserRoleId = typeof rawEvidence.guestUserRoleId === 'string' ? rawEvidence.guestUserRoleId : undefined;
  evidence.guestUserRoleName =
    typeof rawEvidence.guestUserRoleName === 'string' ? rawEvidence.guestUserRoleName : undefined;
  evidence.acceptLimitedGuestRole = rawEvidence.acceptLimitedGuestRole ?? false;
  evidence.pass = rawEvidence.pass ?? false;

  return evidence;
};
